// src/main.js — entry point do app
//
// Fluxo de boot:
// 1. Importa estilos base (tokens + base)
// 2. Inicializa auth (verifica sessão persistida, registra listeners)
// 3. Se hidratado → carrega shell apropriado por tipo_usuario (lazy)
// 4. Se não hidratado → carrega módulo auth (lazy) e mostra tela de login
//
// 🔒 Multi-tenancy:
// - O tenantContext é a única fonte da verdade
// - main.js NÃO toca em window globals — usa imports

import { initAuth } from './context/authContext.js';
import { tenant } from './context/tenantContext.js';
import { eventBus } from './context/eventBus.js';
import { installSvgSymbols } from './lib/svgSymbols.js';
import { registerRoute, startRouter, navigate } from './router/router.js';
import { mountShell, unmountShell, updateActive } from './components/sidebar.js';
import { mountChamadoFab } from './modules/helpdesk/novoChamado.js';
import { initObservability, setObservabilityUser, clearObservabilityUser } from './lib/observability.js';
import { showToast } from './lib/toast.js';
import { initResumoFx } from './lib/resumoFx.js';

// CSS base · sempre carregado (tokens + reset + layout shell)
import './assets/styles/_tokens.css';
import './assets/styles/_base.css';
// ⭐ CSS CANÔNICO · design system completo. Paleta migrada para MotionGenie
// (Proposta 2): roxo #7C5CFC / status vivos / neutros frios. tokens-motion.css
// é importado depois para reforçar a cascata nos dois temas (A claro / C escuro).
import './assets/styles/_canonical.css';
// 🎨 Identidade visual "MotionGenie" (Proposta 2, token-first) · importado por ÚLTIMO
// para vencer a cascata. Claro=Opção A · Escuro=Opção C. Reversível: comente para voltar.
import './assets/styles/tokens-motion.css';
import './assets/styles/motion-restyle.css';
import './assets/styles/dashboard-layout.css';
import './assets/styles/layout-list.css';     // casca única dos tipos de tela (decisão 04/07 — .prog-*/.panels-* aposentados sem uso)
import './assets/styles/tokens-bi.css';
// 💧 Skin "Liquid Glass" (one-button, 04/07) · por último: só age com html[data-skin]
// ── REDESIGN SHADCN (casca 2.0, escopado em .sc-scope — não afeta o legado) ──
import './assets/styles/tokens-shadcn.css';
import './assets/styles/layout-shadcn.css';
import './assets/styles/components-shadcn.css';

// ── Skin inicial (one-button) · antes do primeiro render, sem flash ───────
import { bootSkin } from './lib/skin.js';
bootSkin();
// Troca de skin re-renderiza a rota atual (charts releem tokens via getComputedStyle)
window.addEventListener('theme:changed', () => {
  try {
    import('./router/router.js').then(({ getCurrentRoute, navigate: nav }) => {
      const r = getCurrentRoute(); nav(r.page, r.params);
    });
  } catch (_) {}
});

// ── Tema inicial · respeita preferência salva, senão prefers-color-scheme ─
(function _bootTheme() {
  try {
    // Skin ativa (data-skin): claro/escuro legado NÃO se aplica — re-adicionar
    // html.light aqui recriava o híbrido com o canonical no reload (bug visto 05/07).
    if (document.documentElement.dataset.skin) return;
    const saved = localStorage.getItem('angular-theme'); // 'light' | 'dark'
    const prefersLight = window.matchMedia?.('(prefers-color-scheme: light)').matches;
    const useLight = saved ? saved === 'light' : prefersLight;
    document.documentElement.classList.toggle('light', !!useLight);
  } catch (_) {}
})();

// ── Registro de rotas (lazy import por chunk) ────────────────
registerRoute('dashboard', () => import('./modules/dashboard/index.js'));
registerRoute('piloto', () => import('./modules/piloto/index.js')); // REDESIGN shadcn (etapa 3)
registerRoute('obras', () => import('./modules/obras/index.js'));
registerRoute('obra', () => import('./modules/obras/detailEntry.js'));
registerRoute('financeiro', () => import('./modules/financeiro/index.js'));
registerRoute('dre', () => import('./modules/dre/index.js'));
registerRoute('inadimplentes', () => import('./modules/inadimplentes/index.js'));
registerRoute('contas_receber', () => import('./modules/contasReceber/index.js'));
registerRoute('contas_pagar', () => import('./modules/contasPagar/index.js'));
registerRoute('recorrencias', () => import('./modules/recorrencias/index.js'));
registerRoute('meus_chamados', () => import('./modules/helpdesk/meusChamados.js'));
registerRoute('despesas', () => import('./modules/despesas/index.js'));
registerRoute('reembolsos', () => import('./modules/reembolsos/index.js'));
registerRoute('notas', () => import('./modules/notas/index.js'));
registerRoute('compras', () => import('./modules/compras/index.js'));
registerRoute('nfse_tomadas', () => import('./modules/nfseTomadas/index.js'));
registerRoute('rh', () => import('./modules/rh/index.js'));
registerRoute('funcionarios', () => import('./modules/funcionarios/index.js'));
registerRoute('docs_funcionarios', () => import('./modules/documentos/index.js'));
registerRoute('ausencias', () => import('./modules/ausencias/index.js'));
registerRoute('funcionario_perfil', () => import('./modules/funcionarioPerfil/index.js'));
registerRoute('ferias_rh', () => import('./modules/ferias/index.js'));
registerRoute('ponto', () => import('./modules/ponto/index.js'));
registerRoute('horas', () => import('./modules/horas/index.js'));
registerRoute('espelho_ponto', () => import('./modules/espelhoPonto/index.js'));
registerRoute('cronograma', () => import('./modules/cronograma/index.js'));
registerRoute('obras_entregues', () => import('./modules/obrasEntregues/index.js'));
registerRoute('garantias', () => import('./modules/garantias/index.js'));
registerRoute('etapas', () => import('./modules/etapas/index.js'));
registerRoute('clientes', () => import('./modules/clientes/index.js'));
registerRoute('fornecedores', () => import('./modules/fornecedores/index.js'));
registerRoute('terceirizados', () => import('./modules/terceirizados/index.js'));
registerRoute('contratos', () => import('./modules/contratos/index.js'));
registerRoute('orcamentos', () => import('./modules/orcamentos/index.js'));
registerRoute('portfolio', () => import('./modules/portfolio/index.js'));
registerRoute('tarefas', () => import('./modules/tarefas/index.js'));
// F7 · BI Premium + Mapa Premium + Central de relatórios
registerRoute('bi',         () => import('./modules/bi/index.js'));
registerRoute('mapa',       () => import('./modules/mapas/index.js'));
registerRoute('relatorios', () => import('./modules/relatorios/index.js'));
registerRoute('arquivos', () => import('./modules/arquivos/index.js'));
registerRoute('usuarios', () => import('./modules/usuarios/index.js'));
registerRoute('sobre', () => import('./modules/sobre/index.js'));
registerRoute('configuracoes', () => import('./modules/configuracoes/index.js'));
registerRoute('atividades', () => import('./modules/atividades/index.js'));
registerRoute('upgrade', () => import('./modules/planos/index.js'));
registerRoute('assistente', () => import('./modules/assistente/index.js')); // feature 003 · hub CFO de Bolso
registerRoute('wirekit', () => import('./modules/wirekit/index.js')); // base · galeria de componentes padronizados

// Atualiza item ativo da sidebar a cada navegação
window.addEventListener('hashchange', () => updateActive());

// Rede de segurança · QR antigo (#verificar?id=) com o app já carregado/logado:
// mudar só o hash não roda o boot(), então força reload p/ cair na rota pública de verificação.
window.addEventListener('hashchange', () => {
  if (/^#verificar(\b|[/?#]|$)/.test(location.hash) && !document.getElementById('vrf-style')) location.reload();
});

// ── boot ─────────────────────────────────────────────────────────────

async function boot() {
  // 0. Rota PÚBLICA de verificação (#verificar?id=) — Feature 013.
  //    Acessível SEM login: curto-circuita o boot antes de qualquer fluxo de auth.
  //    A RPC rdo_verificar é GRANT anon e devolve dado mínimo com nomes mascarados (LGPD).
  if (/^#verificar(\b|[/?#]|$)/.test(location.hash)) {
    try { const mod = await import('./modules/verificacao/index.js'); await mod.render(); }
    catch (e) { console.error('[boot] verificar falhou:', e); }
    window.addEventListener('hashchange', () => { if (/^#verificar/.test(location.hash)) location.reload(); });
    return;
  }

  // 1. SVG sprite global
  installSvgSymbols();
  try { initResumoFx(); } catch (e) {}

  // 1b. Observabilidade de erros (GlitchTip) — cedo, antes de qualquer fluxo.
  try { initObservability(); } catch (e) {}

  // 2. Registra listeners ANTES de iniciar auth — assim qualquer sessão
  //    encontrada por initAuth dispara 'auth:signedin' que troca pra shell.
  eventBus.on('auth:signedout', renderAuthScreen);
  eventBus.on('auth:signedin', async () => {
    await _teardownAuthIfPresent();
    await renderForRole();
    try { setObservabilityUser(tenant.user, tenant.profile); } catch (e) {}
  });

  // 3. Race rápida: getSession com timeout de 1.5s (não bloqueia UX).
  const sessionOrTimeout = await Promise.race([
    _quickSessionCheck(),
    new Promise((res) => setTimeout(() => res({ timeout: true }), 1500)),
  ]);

  // Hash de ERRO de auth (link de e-mail inválido/expirado): o Supabase devolve
  // #error=...&error_code=...&error_description=... — sem isto, o boot ignorava o
  // erro e caía numa sessão cacheada (ex.: outro usuário logado no navegador).
  // Mostra mensagem humana e leva pra tela de login. (Polish Pattern §E)
  const _hp = new URLSearchParams(location.hash.replace(/^#/, '').replace(/#/g, '&')); // tolera hash duplo
  const _hashErr = _hp.get('error') || _hp.get('error_code');
  if (_hashErr) {
    const code = _hp.get('error_code') || _hp.get('error') || '';
    const msg = /otp_expired|expired/i.test(code)
      ? 'O link de e-mail expirou ou já foi usado. Solicite um novo.'
      : 'Não foi possível validar o link de e-mail. Solicite um novo.';
    try { history.replaceState(null, '', location.pathname); } catch (_) {}
    await renderAuthScreen();
    try { showToast(msg, 'error'); } catch (_) {}
    return;
  }

  const isRecoveryFlow = location.hash.includes('type=recovery');

  if (isRecoveryFlow) {
    // Recovery SEM sessão (iteração final do P1-4): o access_token da URL é usado
    // DIRETO no PUT /auth/v1/user (novaSenha.js) — nada de setSession/signOut no boot.
    // Motivo: log do GoTrue provou que setSession tenta refresh e morre com
    // 'Refresh Token Not Found' em link real. Token direto = determinístico,
    // imune a sessão suja (troca SEMPRE a senha do dono do token da URL).
    let _recAt = null;
    try {
      // normaliza hash DUPLO ('#type=recovery#access_token=...') de links antigos
      const rp = new URLSearchParams(location.hash.replace(/^#/, '').replace(/#/g, '&'));
      _recAt = rp.get('access_token');
    } catch (e) { console.warn('[boot] recovery parse falhou:', e); }
    if (_recAt) window.__recoveryAccessToken = _recAt;
    await renderAuthScreen();
    if (!_recAt) {
      try { showToast('Link de redefinição inválido ou expirado. Solicite um novo.', 'error'); } catch (_) {}
    }
    return;
  } else if (sessionOrTimeout?.session?.user) {
    initAuth().catch((err) => console.warn('[boot] initAuth bg falhou:', err));
    await _waitHydrated(2000);
    if (tenant.isHydrated) {
      await renderForRole();
    } else {
      await renderAuthScreen();
    }
  } else {
    await renderAuthScreen();
    initAuth().catch((err) => console.warn('[boot] initAuth bg falhou:', err));
  }
}

async function _quickSessionCheck() {
  try {
    const { sb } = await import('./api/supabaseClient.js');
    const { data } = await sb.auth.getSession();
    return data;
  } catch (err) {
    console.warn('[boot] getSession quick falhou:', err);
    return null;
  }
}

async function _waitHydrated(maxMs) {
  const t0 = Date.now();
  while (!tenant.isHydrated && Date.now() - t0 < maxMs) {
    await new Promise((r) => setTimeout(r, 50));
  }
}

async function _teardownAuthIfPresent() {
  if (!document.body.classList.contains('modo-auth')) return;
  try {
    const mod = await import('./modules/auth/index.js');
    mod.teardown?.();
  } catch (err) {
    console.warn('[main] auth teardown falhou:', err);
  }
}

// Listener de logout · desmonta sidebar
eventBus.on('auth:signedout', () => { unmountShell(); try { import('./lib/ilhaNav.js').then((m) => m.unmountIlha()); } catch (e) {} try { clearObservabilityUser(); } catch (e) {} });

async function renderForRole() {
  // 2FA Fase 2: se a sessão tem fator TOTP e está em AAL1, desafia ANTES de tudo.
  try {
    const { mfaAalStatus, renderMfaChallenge } = await import('./modules/seguranca/twoFactor.js');
    const aal = await mfaAalStatus();
    if (aal.precisaDesafio) return renderMfaChallenge();
  } catch (e) { /* MFA indisponível → segue sem desafio (fail-open só p/ infra, não p/ código inválido) */ }

  // 2FA Fase 1: força troca da senha temporária no 1º acesso (some quando a flag é limpa).
  if (tenant.profile?.must_change_password) {
    const mod = await import('./modules/auth/forcarTrocaSenha.js');
    return mod.renderForcarTrocaSenha();
  }
  // Shell decidida pelos PAPEIS (RBAC), nao pelo tipo_usuario legado (incidente 09/06).
  // Admin/escritorio -> ERP (router desktop). Campo/cliente -> shells dedicadas.
  const tipo = tenant.profile?.tipo_usuario;

  // Feature 004 · Gate de liberação do Trial: empresa não-liberada → tela de código.
  // super_admin nunca é bloqueado; o RLS já tranca os dados no servidor.
  if (tipo !== 'super_admin' && tenant.config && tenant.config.liberado === false) {
    const mod = await import('./modules/liberacao/index.js');
    return mod.render();
  }

  // super_admin: console da plataforma.
  if (tipo === 'super_admin') {
    const mod = await import('./modules/saas/index.js');
    return mod.render();
  }

  switch (_shellAlvo(tenant.roles || [], tipo)) {
    case 'cliente': {
      const mod = await import('./modules/shell-cliente/index.js');
      try { mountChamadoFab(); } catch (e) {}
      return mod.render();
    }
    case 'supervisor': {
      const mod = await import('./modules/shell-supervisor/index.js');
      try { mountChamadoFab(); } catch (e) {}
      return mod.render();
    }
    case 'funcionario': {
      const mod = await import('./modules/shell-funcionario/index.js');
      try { mountChamadoFab(); } catch (e) {}
      return mod.render();
    }
    case 'admin': {
      const mod = await import('./modules/shell-admin/index.js');
      try { mountChamadoFab(); } catch (e) {}
      return mod.render();
    }
    case 'gestor': {
      const mod = await import('./modules/shell-gestor/index.js');
      try { mountChamadoFab(); } catch (e) {}
      return mod.render();
    }
    case 'erp':
    default: {
      mountShell();
      // Navegação desktop conforme preferência do usuário (Configurações › Perfil)
      try { const { applyNav, currentNav } = await import('./lib/navPref.js'); await applyNav(currentNav()); } catch (e) {}
      try { mountChamadoFab(); } catch (e) {}
      if (!location.hash || location.hash === '#') {
        location.hash = '#dashboard';
      }
      startRouter();
      return;
    }
  }
}

// Decide a shell a partir dos papeis RBAC (feature 002 + incidente 09/06).
// Decisao de produto: no MOBILE, papel de CAMPO (supervisor_obras/funcionario_campo) tem
// precedencia sobre papeis de escritorio; no DESKTOP, escritorio/admin abre o ERP.
// Fallback p/ tipo_usuario legado se o usuario nao tiver papeis no RBAC (nao-backfillado).
function _shellAlvo(roles, tipo) {
  const has = (r) => roles.includes(r);
  const isMobile = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width:768px)').matches;

  if (!roles.length) {
    if (tipo === 'cliente') return 'cliente';
    if (tipo === 'supervisor') return 'supervisor';
    if (tipo === 'funcionario') return 'funcionario';
    return isMobile ? 'admin' : 'erp'; // administrador / legado
  }

  // Mobile: campo tem precedencia mesmo se o usuario tambem for de escritorio.
  if (isMobile && has('supervisor_obras')) return 'supervisor';
  if (isMobile && has('funcionario_campo')) return 'funcionario';

  // Admin: desktop = ERP completo; mobile = shell-admin (read-only/oversight · feature 011).
  if (has('tenant_admin')) return isMobile ? 'admin' : 'erp';

  // Escritorio (gestor com/fin e RH): desktop = ERP; mobile = shell-gestor (read + aprovacoes · feature 011).
  if (has('gestor_comercial_financeiro') || has('funcionario_rh')) return isMobile ? 'gestor' : 'erp';

  // Campo puro.
  if (has('supervisor_obras')) return 'supervisor';
  if (has('funcionario_campo')) return 'funcionario';
  if (has('cliente_final')) return 'cliente';

  // Default mais restritivo: NUNCA admin sem papel que justifique.
  return 'funcionario';
}

async function renderAuthScreen() {
  // Limpa o chrome dos shells mobile que vive no document.body (ilha + navs) ao deslogar/voltar pro login
  document.getElementById('mobile-topbar')?.remove();
  document.getElementById('func-bottom-nav')?.remove();
  document.getElementById('cliente-bottom-nav')?.remove();
  document.getElementById('supervisor-bottom-nav')?.remove();
  document.body.classList.remove('modo-cliente', 'modo-funcionario', 'modo-supervisor');
  const mod = await import('./modules/auth/index.js');
  return mod.render();
}


// ── kick off boot ────────────────────────────────────────
boot().catch((err) => console.error('[boot] erro fatal:', err));

if (import.meta.env.DEV || import.meta.env.MODE === 'staging') {
  window.__tenant = tenant;
  window.__navigate = navigate;
}
